import React, {ComponentProps, FC, PropsWithChildren, ReactNode, useState} from 'react';
import styled from 'styled-components';

import {KeyboardActionsEnum} from '../../../../libs/shared/core/src/helpers/browser/keyboardHelpers';
import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {useOnRequestClose} from '../../../../libs/shared/core/src/helpers/react/hooks/hookHelpers';
import {ButtonVariantsEnum} from '../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {ButtonGroupProps} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroup';
import {ButtonGroupSpacer} from '../../../../libs/shared/ui/src/components/buttons/buttonGroup/buttonGroupSpacer';
import {IconButton} from '../../../../libs/shared/ui/src/components/buttons/iconButton';
import {SameWidthButton} from '../../../../libs/shared/ui/src/components/buttons/sameWidthButton';
import {Dialog} from '../../../../libs/shared/ui/src/components/dialogs/dialog';
import {DialogButtonGroup} from '../../../../libs/shared/ui/src/components/dialogs/dialogButtonGroup';
import {DialogContent} from '../../../../libs/shared/ui/src/components/dialogs/dialogContent';
import {
  DialogHeader,
  DialogHeaderProps,
} from '../../../../libs/shared/ui/src/components/dialogs/dialogHeader';
import {KeyboardShortcuts} from '../../../../libs/shared/ui/src/components/keyboard/keyboardShortcuts';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {EducationBanner, EducationBannerProps} from '../educationBanner/educationBanner';

/*
 * Props.
 */

interface DialogWithInfoHeaderProps
  extends ComponentProps<typeof Dialog>,
    DialogHeaderProps,
    ButtonGroupProps,
    Omit<EducationBannerProps, 'onCloseEducationBanner'> {
  /** The text of the primary button. */
  primaryButtonLabel: ReactNode;
  /** Event handler for the primary button. */
  onPrimaryClick?: () => void;
  isPrimaryButtonDisabled?: boolean;
  shouldCloseOnPrimaryClick?: boolean;
  /** The text of the secondary button. If this is available then we'll render a third button on the left */
  secondaryButtonLabel?: ReactNode;
  /** Event handler for the secondary button. */
  onSecondaryClick?: () => void;
  shouldCloseOnSecondaryClick?: boolean;
  isSecondaryButtonDisabled?: boolean;
  secondaryButtonVariant?: ButtonVariantsEnum;
  shouldShowInfoByDefault: boolean;
}

/*
 * Style.
 */

const StyledEducationBanner = styled(EducationBanner)`
  margin-bottom: 20px;
`;

const StyledDialogContent = styled(DialogContent)`
  flex: 1;
  overflow: auto;
  margin: 0;
  padding: 20px 20px;
`;

const StyledDialogButtonGroupContainerDiv = styled.div`
  padding-top: 20px;
  border-top: 1px solid ${(p) => p.theme.greys.shade30};
`;

/*
 * Component.
 */

/** Dialog for when two actions are needed in addition to a cancel button. */
export const DialogWithInfoHeader: FC<PropsWithChildren<DialogWithInfoHeaderProps>> = (props) => {
  const theme = useUserTheme();
  const {title, onPrimaryClick, isPrimaryButtonDisabled, secondaryButtonLabel, onSecondaryClick} = props;
  const [isInfoBoxExpanded, setIsInfoBannerExpanded] = useState(props.shouldShowInfoByDefault);
  const iconColor = isInfoBoxExpanded ? theme.palette.blue.shade40 : theme.palette.grey.shade50;
  const onRequestClose = useOnRequestClose(props.onRequestClose);

  const onPrimaryClickWithClose = () => {
    if (onPrimaryClick) {
      onPrimaryClick();
    }

    onRequestClose();
  };

  const onSecondaryClickWithClose = () => {
    if (onSecondaryClick) {
      onSecondaryClick();
    }

    onRequestClose();
  };

  const keyHandlers = {
    [KeyboardActionsEnum.CONFIRM]: onPrimaryClickWithClose,
  };

  return (
    <Dialog {...props}>
      <KeyboardShortcuts handlers={keyHandlers}>
        <DialogHeader
          title={title}
          customButtons={
            <IconButton
              iconName={isInfoBoxExpanded ? 'infoFilled' : 'info'}
              label={<Trans id="dialog-with-info-header-more-info-button">More information</Trans>}
              onClick={() => setIsInfoBannerExpanded(!isInfoBoxExpanded)}
              color={iconColor}
              hoverColor={iconColor}
            />
          }
        />
        <StyledDialogContent>
          {isInfoBoxExpanded && (
            <StyledEducationBanner
              educationBannerTitle={props.educationBannerTitle}
              learnMoreUrl={props.learnMoreUrl}
              leftButton={props.leftButton}
              onCloseEducationBanner={() => setIsInfoBannerExpanded(false)}
              educationBannerContent={props.educationBannerContent}
            />
          )}
          {props.children}
        </StyledDialogContent>
        <StyledDialogButtonGroupContainerDiv>
          <DialogButtonGroup {...props}>
            {secondaryButtonLabel && (
              <SameWidthButton
                variant={props.secondaryButtonVariant}
                onClick={props.shouldCloseOnSecondaryClick ? onSecondaryClickWithClose : onSecondaryClick}
                isDisabled={props.isSecondaryButtonDisabled}
              >
                {secondaryButtonLabel}
              </SameWidthButton>
            )}
            <ButtonGroupSpacer />

            <SameWidthButton onClick={props.onRequestClose}>
              <Trans id="dialog-cancel">Cancel</Trans>
            </SameWidthButton>
            <SameWidthButton
              variant={ButtonVariantsEnum.PRIMARY}
              onClick={props.shouldCloseOnPrimaryClick ? onPrimaryClickWithClose : onPrimaryClick}
              isDisabled={isPrimaryButtonDisabled}
            >
              {props.primaryButtonLabel}
            </SameWidthButton>
          </DialogButtonGroup>
        </StyledDialogButtonGroupContainerDiv>
      </KeyboardShortcuts>
    </Dialog>
  );
};
